import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for Invoice List tabs
 * Shows key features of the invoice management interface
 */
export const useInvoiceListTour = (
    tabType:
        | 'index'
        | 'waiting-confirm'
        | 'approved'
        | 'invoicable'
        | 'non-invoicable'
        | 'deposit'
) => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getTourSteps = (): TourStep[] => {
        const commonSteps: TourStep[] = [
            {
                selector: '[data-tour="invoice-filter-bar"]',
                title: 'Bộ lọc hoá đơn',
                description:
                    'Lọc hoá đơn theo nhiều tiêu chí:\n• Trạng thái hoá đơn\n• Nhóm sản phẩm\n• Account Manager\n• Khoảng thời gian',
            },
            {
                selector: '[data-tour="invoice-search"]',
                title: 'Tìm kiếm nhanh',
                description:
                    'Tìm kiếm hoá đơn theo:\n• Mã hoá đơn\n• Tên khách hàng\n• Mã dịch vụ',
            },
            {
                selector: '[data-tour="invoice-data-table"]',
                title: 'Bảng dữ liệu hoá đơn',
                description:
                    'Bảng hiển thị danh sách hoá đơn:\n• Click để xem chi tiết\n• Sắp xếp theo cột\n• Resize cột tuỳ ý',
            },
            {
                selector: '[data-tour="invoice-settings"]',
                title: 'Cài đặt bảng',
                description:
                    'Tuỳ chỉnh hiển thị:\n• Cỡ chữ\n• Chiều cao hàng\n• Ẩn/hiện cột',
            },
        ]

        // Tab-specific steps
        switch (tabType) {
            case 'index':
                return [
                    {
                        selector: '[data-tour="invoice-list"]',
                        title: 'Danh sách hoá đơn',
                        description:
                            'Trang quản lý tất cả hoá đơn.\n• Xem tổng quan thống kê\n• Lọc và tìm kiếm\n• Theo dõi trạng thái',
                    },
                    {
                        selector: '[data-tour="invoice-statistical"]',
                        title: 'Thống kê hoá đơn',
                        description:
                            'Xem tổng quan hoá đơn:\n• Tổng số hoá đơn\n• Tổng doanh thu\n• Theo trạng thái',
                    },
                    ...commonSteps,
                ]

            case 'waiting-confirm':
                return [
                    {
                        selector: '[data-tour="invoice-list"]',
                        title: 'Hoá đơn chờ xác nhận',
                        description:
                            'Danh sách hoá đơn cần xác nhận doanh thu.\n• Chọn nhiều hoá đơn\n• Duyệt xuất HĐ / không xuất HĐ',
                    },
                    ...commonSteps,
                    {
                        selector: '[data-tour="invoice-data-table"]',
                        title: 'Duyệt hoá đơn hàng loạt',
                        description:
                            'Tick chọn hoá đơn cần duyệt:\n• Chọn "Xuất HĐ" để duyệt và xuất hoá đơn\n• Chọn "Không xuất HĐ" để duyệt nhưng không xuất',
                    },
                ]

            case 'invoicable':
                return [
                    {
                        selector: '[data-tour="invoice-list"]',
                        title: 'Hoá đơn cần xuất',
                        description:
                            'Danh sách hoá đơn đã duyệt và cần xuất hoá đơn điện tử.\n• Hoá đơn đã xác nhận doanh thu\n• Cần nhập số HĐ từ MISA',
                    },
                    ...commonSteps,
                    {
                        selector: '[data-tour="invoice-data-table"]',
                        title: 'Nhập số hoá đơn MISA',
                        description:
                            'Sau khi xuất HĐ từ MISA, nhập số HĐ vào đây:\n\n1. Hover vào cột "Mã xuất HĐ"\n2. Click icon bút chì (✏️)\n3. Nhập số HĐ từ MISA\n4. Nhấn Enter để lưu\n\n**Mẹo:**\n• Nhấn Esc để huỷ\n• Số HĐ được lưu tự động',
                    },
                ]

            case 'non-invoicable':
                return [
                    {
                        selector: '[data-tour="invoice-list"]',
                        title: 'Hoá đơn không xuất',
                        description:
                            'Danh sách hoá đơn đã duyệt nhưng không xuất HĐ điện tử.\n• Đã xác nhận doanh thu\n• Không cần xuất HĐ',
                    },
                    ...commonSteps,
                ]

            case 'deposit':
                return [
                    {
                        selector: '[data-tour="invoice-list"]',
                        title: 'Hoá đơn nạp tiền',
                        description:
                            'Danh sách hoá đơn nạp tiền (deposit).\n• Hoá đơn từ nạp tiền\n• Theo dõi trạng thái thanh toán',
                    },
                    ...commonSteps,
                ]

            default:
                return commonSteps
        }
    }

    const getVisibleSteps = useCallback(() => {
        return getTourSteps().filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [tabType])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useInvoiceListTour
